import React, { useState, useEffect } from 'react';
import { db, collection, getDocs, updateDoc, doc, deleteDoc } from '../../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCheckCircle,
  faEnvelope,
  faTimesCircle, 
  faTrash,
  faToggleOn,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';

const BCCEmailList = () => {
  const [bccEmails, setBccEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchBccEmails = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, "bccEmails"));
      const data = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setBccEmails(data);
    } catch (err) {
      console.error("Error fetching BCC emails: ", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBccEmails();
  }, []);

  const handleToggle = async (id, enabled) => {
    try {
      const emailDocRef = doc(db, "bccEmails", id);
      await updateDoc(emailDocRef, { enabled: !enabled });
      setBccEmails(prevEmails =>
        prevEmails.map(email =>
          email.id === id ? { ...email, enabled: !enabled } : email
        )
      );
    } catch (err) {
      console.error("Error updating BCC email: ", err);
      setError(err.message);
    }
  };

  const handleDeleteEmail = async (id) => {
    if (window.confirm("Are you sure you want to delete this email?")) {
      setIsDeleting(true);
      try {
        await deleteDoc(doc(db, "bccEmails", id));
        setBccEmails(prevEmails => prevEmails.filter(email => email.id !== id));
      } catch (err) {
        console.error("Error deleting BCC email: ", err);
        setError(err.message);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center space-y-4">
          <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-4xl" />
          <span className="text-gray-500">Loading emails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 border border-red-200">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faTimesCircle} className="text-red-400 mr-3" />
          <p className="text-red-700">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {bccEmails.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="text-gray-400 mb-4">
            <FontAwesomeIcon icon={faEnvelope} size="3x" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No BCC Emails</h3>
          <p className="text-gray-500">Add your first BCC email to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {bccEmails.map((email) => (
                <tr key={email.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{email.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{email.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${email.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {email.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {email.createdAt ? new Date(email.createdAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleToggle(email.id, email.enabled)}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md mr-2 transition-colors
                        ${email.enabled 
                          ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' 
                          : 'text-green-700 bg-green-50 hover:bg-green-100'
                        }`}
                    >
                      <FontAwesomeIcon 
                        icon={email.enabled ? faToggleOff : faToggleOn} 
                        className="mr-1.5" 
                      />
                      {email.enabled ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDeleteEmail(email.id)}
                      disabled={isDeleting}
                      className="inline-flex items-center px-3 py-1.5 text-red-700 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                    >
                      <FontAwesomeIcon icon={faTrash} className="mr-1.5" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BCCEmailList; 